import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Upload, FileText, Loader2, CloudCog, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";



export default function UploadPage({ user }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Upload form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [roleId, setRoleId] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Greenhouse
  const [ghLimit, setGhLimit] = useState("200");
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const resp = await api.get("/roles");
        setRoles(resp.data);
      } catch (e) {}
    };
    fetchRoles();
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Candidate name is required");
      return;
    }
    if (!file) {
      toast.error("Please select a resume file");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", name);
      formData.append("email", email);
      formData.append("phone", phone);
      formData.append("role_id", roleId || "");

      await api.post("/candidates/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success(`Uploaded ${name}'s resume successfully`);
      setName("");
      setEmail("");
      setPhone("");
      setRoleId("");
      setFile(null);
    } catch (e) {
      const msg = e.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleGreenhouseSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const resp = await api.post("/greenhouse/sync", { 
        limit: parseInt(ghLimit) || 200,
      }, { timeout: 120000 });
      setSyncResult(resp.data);
      toast.success(resp.data.message);
    } catch (e) {
      const msg = e.response?.data?.detail || "Sync failed";
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const handleRefreshResumes = async () => {
    setRefreshing(true);
    try {
      const resp = await api.post("/greenhouse/refresh-resumes", {}, { timeout: 120000 });
      toast.success(resp.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const handleBatchParse = async (force = false) => {
    setParsing(true);
    setParseResult(null);
    try {
      const resp = await api.post("/candidates/batch-parse", { limit: 20, force }, { timeout: 120000 });
      setParseResult(resp.data);
      toast.success(resp.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Parsing failed");
    } finally {
      setParsing(false);
    }
  };

  return (
    <div className="px-4 py-6 sm:p-6 md:p-8 lg:p-12 space-y-6 lg:space-y-8 animate-fade-in" data-testid="upload-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white uppercase">Upload Candidates</h1>
        <p className="text-zinc-500 mt-1">Upload resumes manually or sync from Greenhouse</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8">
        {/* Manual Upload */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-lg font-semibold text-white uppercase tracking-tight flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-400" /> Manual Upload
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Name *</label>
                <Input
                  data-testid="upload-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Candidate full name"
                  className="bg-zinc-800 border-white/10 text-zinc-200"
                  required
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Email</label>
                <Input
                  data-testid="upload-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="candidate@email.com"
                  className="bg-zinc-800 border-white/10 text-zinc-200"
                  type="email"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Phone</label>
                <Input
                  data-testid="upload-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="bg-zinc-800 border-white/10 text-zinc-200"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Role</label>
                <Select value={roleId} onValueChange={setRoleId}>
                  <SelectTrigger className="bg-zinc-800 border-white/10 text-zinc-300" data-testid="upload-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-white/10">
                    {roles.map((r) => (
                      <SelectItem key={r.role_id} value={r.role_id}>{r.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Resume (PDF) *</label>
                <div className="relative">
                  <input
                    data-testid="upload-file"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="hidden"
                    id="resume-upload"
                  />
                  <label
                    htmlFor="resume-upload"
                    className="flex items-center gap-3 p-4 rounded-lg border border-dashed border-white/10 bg-zinc-800/50 cursor-pointer hover:border-blue-500/40 transition-colors"
                  >
                    {file ? (
                      <>
                        <FileText className="w-5 h-5 text-blue-400" />
                        <span className="text-sm text-zinc-300">{file.name}</span>
                        <Check className="w-4 h-4 text-emerald-400 ml-auto" />
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-zinc-500" />
                        <span className="text-sm text-zinc-500">Click to select a PDF resume</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
              <Button
                data-testid="upload-submit-btn"
                type="submit"
                disabled={uploading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing Resume...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" /> Upload & Parse Resume
                  </>
                )}
              </Button>
              <p className="text-xs text-zinc-600 text-center">
                PDF resumes will be automatically parsed using AI (Gemini Flash)
              </p>
            </form>
          </CardContent>
        </Card>

        {/* Greenhouse Sync */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-lg font-semibold text-white uppercase tracking-tight flex items-center gap-2">
              <CloudCog className="w-5 h-5 text-emerald-400" /> Greenhouse Sync
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 rounded-lg bg-zinc-800/50 border border-white/5 space-y-3">
              <h4 className="text-sm font-medium text-zinc-300">How it works</h4>
              <ul className="space-y-2 text-xs text-zinc-500">
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 font-mono">1.</span> Uses stored Greenhouse API credentials (configure in Settings)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 font-mono">2.</span> Pulls only engineering role candidates
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 font-mono">3.</span> Excludes candidates in Offer or Hired stage
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-400 font-mono">4.</span> Candidates appear in your review queue
                </li>
              </ul>
            </div>

            <div>
              <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Import Limit</label>
              <Input
                data-testid="greenhouse-limit"
                type="number"
                value={ghLimit}
                onChange={(e) => setGhLimit(e.target.value)}
                placeholder="200"
                className="bg-zinc-800 border-white/10 text-zinc-200 w-32"
              />
              <p className="text-xs text-zinc-600 mt-1">Max engineering candidates to import per sync</p>
            </div>

            <Button
              data-testid="greenhouse-sync-btn"
              onClick={handleGreenhouseSync}
              disabled={syncing}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {syncing ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Syncing...</>
              ) : (
                <><CloudCog className="w-4 h-4 mr-2" /> Sync Engineering Candidates</>
              )}
            </Button>

            <div className="grid grid-cols-2 gap-2">
              <Button
                data-testid="refresh-resumes-btn"
                onClick={handleRefreshResumes}
                disabled={refreshing}
                variant="outline"
                className="border-white/10 text-zinc-300 hover:text-white"
                size="sm"
              >
                {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
                Refresh Resume URLs
              </Button>
              <Button
                data-testid="batch-parse-btn"
                onClick={() => handleBatchParse(false)}
                disabled={parsing}
                variant="outline"
                className="border-blue-500/30 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                size="sm"
              >
                {parsing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
                Parse New
              </Button>
            </div>
            <Button
              data-testid="reparse-btn"
              onClick={() => handleBatchParse(true)}
              disabled={parsing}
              variant="outline"
              className="w-full border-amber-500/30 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
              size="sm"
            >
              {parsing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
              Re-parse with Full Work History (20 candidates)
            </Button>

            {parseResult && (
              <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 space-y-1">
                <p className="text-sm font-medium text-blue-400">Parse Complete</p>
                <p className="text-xs text-zinc-400">Parsed: <span className="font-mono text-white">{parseResult.parsed}</span> | Failed: <span className="font-mono text-white">{parseResult.failed}</span></p>
              </div>
            )}

            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-400/80">
                Only engineering roles are synced. Resume parsing uses Gemini AI.
              </p>
            </div>

            {syncResult && (
              <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20 space-y-1" data-testid="sync-result">
                <p className="text-sm font-medium text-emerald-400">Sync Complete</p>
                <p className="text-xs text-zinc-400">Imported: <span className="font-mono text-white">{syncResult.imported}</span></p>
                <p className="text-xs text-zinc-400">Skipped (existing): <span className="font-mono text-white">{syncResult.skipped}</span></p>
                <p className="text-xs text-zinc-400">Total found: <span className="font-mono text-white">{syncResult.total_found}</span></p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
